// The public embedding API for the WI deterministic sim core (tasks/typescript-port.md,
// "Public embedding API"). One Sim per game; no module-level mutable state, so a DO can
// re-sim many games concurrently in one isolate.
//
//   const sim = Sim.create({ levelName, levelData, templates, anir, seed, roster, multiplayer: true });
//   for (const block of blocks) sim.stepBlock(block.block, block.cmds);  // raw 28-byte wire cmds
//   sim.syncHash()   // the lockstep comparand for the CURRENT block window
//   sim.frameJson()  // the last update's StateFrame::ToJson x-ray (set onFrame to capture all)
//   sim.dumpGobs()   // [gid, side, type, wx, wy] — mirrors WiAuto_GobDump
//
// Embedders: the replay re-sim verifier (sim/tools/resim.mjs), the multiplayer DO
// (post-game hash adjudication), the bot brain (authoritative local world view), and a
// future browser client (real SimHost into a renderer).

import { SimContext } from './context.ts';
import { Simulation, GobStateMachineMgr } from './sim/simulation.ts';
import { SimDriver } from './sim/driver.ts';
import { Level, BindLevelToSimulation, type LevelData } from './level.ts';
import { BuildMgr } from './gob/builder.ts';
import { OvermindGob } from './gob/overmind.ts';
import { InitAllClasses } from './boot.ts';
import { IniReader, type IniSection } from './data/ini.ts';
import { MemoryStream, Stream } from './sm/stream.ts';
import { decodeMessage } from './sm/message.ts';
import { makeAnimRegistry, type AnirJson } from './data/anim-registry.ts';
import './gob/alltypes.ts'; // gob-type registrations (side effect)
import {
  kfPlrReady,
  kfPlrComputer,
  kfPlrComputerOvermind,
  kfPlrUnfulfilled,
  kfPlrCreator,
  kfHcapDefault,
} from './player/side.ts';

export interface SimRosterEntry {
  pid: number;
  side: number;
  name: string;
  /** kfPlr* (game-side Player) flags as recorded in the .wirep roster
   *  (PlayerRecord.wf) — NOT the server-side kfPlrr* set. */
  flags: number;
  bot?: boolean;
  /** The local player (gpplrLocal): ONLY this player's units reveal the fog map
   *  (MobileUnit.cpp ~2970). Fog content is per-client, unhashed, but SAVED — set this
   *  to mirror a specific client's savegame bytes. */
  local?: boolean;
}

export interface SimCreateOptions {
  /** e.g. "m_02.lvl" */
  levelName: string;
  /** The extracted level vector (sim/tools/extract-level.mjs output). */
  levelData: LevelData;
  /** GobTemplates.ini sections (sim/data/GobTemplates.ini.json .sections). */
  templates: IniSection[];
  /** The shipping .anir metadata (sim/data/anir.json). */
  anir: AnirJson;
  /** The recorded game seed (ScBeginGame); SP fresh starts pass 0x29a
   *  (game/game.cpp:1633 SetRandomSeed). */
  seed: number;
  /** The MP roster (PlayersUpdateNetMessage). OMIT for an SP fresh start: the
   *  C++ builds the players from the level's SideInfos instead
   *  (game/game.cpp:1664 gplrm.Init(m_miid.szLvlFilename)). */
  roster?: SimRosterEntry[];
  /** SP only — gwfHandicap for the local player (PlayerMgr::Init's
   *  gpplrLocal->SetHandicap(gwfHandicap), game/Player.cpp:230); default
   *  kfHcapDefault (ht.h:298). */
  spHandicap?: number;
  multiplayer?: boolean;
  /** The lockstep sim-behavior version to run AS: a live game passes its GameParams
   *  dwVersionSimulation; a REPLAY passes its recording's header simVersion so it re-sims with the
   *  behavior it was recorded at. Defaults to the current knVersionSimulation. */
  simVersion?: number;
  /** Tile size of the art generation the game ran with: 32 (default, 832/2432 art) or
   *  24 (824 art) — selects the GetSpecialPoint32 4/3 scaling (sim-quirks A12). */
  gcxTile?: 24 | 32;
  /** Optional presentation host; defaults are no-ops (headless). */
  host?: Partial<SimContext['host']>;
  /** Capture per-update StateFrame ToJson x-rays (onFrame / frameJson()). Off by
   *  default: the serialization is pure diagnostic overhead in production re-sims. */
  frames?: boolean;
  /** Compute the per-update sync hash (the MP desync comparand). Default true. Set false
   *  for read-only re-sims that never compare hashes (the replay viewer's checkpoint
   *  prewarm): the hash is read-only on sim state, so the simulation is bit-identical
   *  while ~19% faster per step. Disables onBlockHash reports + onFrame x-rays. */
  trackState?: boolean;
}

export type GobDump = [gid: number, side: number, type: number, wx: number, wy: number];

/** Magic + version for Sim.snapshot()/restore() — the TS-only COMPLETE resume format
 *  ('WISS' = WI Sim Snapshot). Distinct from the C++ savegame so the two can't be confused. */
const SNAPSHOT_MAGIC = 0x57495353; // 'WISS'
const SNAPSHOT_VERSION = 4; // v2: +residual m_itptPath; v3: +Overmind decision state; v4: +BuildMgr build entries

export class Sim {
  readonly ctx: SimContext;
  readonly sim: Simulation;
  readonly driver: SimDriver;
  private lastFrameJson = '';

  /** Per-update frame callback (the EndTrackState x-ray). */
  onFrame: (cUpdates: number, frameJson: string) => void = () => {};
  /** Per-block hash callback: fires when a block's agreed window closes. */
  onBlockHash: (block: number, hash: number) => void = () => {};

  private constructor(ctx: SimContext, sim: Simulation, driver: SimDriver) {
    this.ctx = ctx;
    this.sim = sim;
    this.driver = driver;
  }

  static create(opts: SimCreateOptions): Sim {
    const ctx = new SimContext();
    ctx.fMultiplayer = opts.multiplayer !== false;
    if (opts.simVersion !== undefined) ctx.simVersion = opts.simVersion;
    ctx.rng.SetRandomSeed(opts.seed | 0);
    ctx.animDataFor = makeAnimRegistry(opts.anir, opts.gcxTile, () => ctx.simVersion);
    if (opts.host) Object.assign(ctx.host, opts.host);

    const sim = new Simulation(ctx);
    ctx.sim = sim;
    const smm = new GobStateMachineMgr(ctx);
    smm.Init(sim);
    ctx.smm = smm;
    const driver = new SimDriver(ctx, sim);
    driver.m_fTrackState = opts.trackState !== false;

    if (!InitAllClasses(ctx, new IniReader(opts.templates))) {
      throw new Error('Sim.create: InitAllClasses failed');
    }

    // ScBeginGame order (proven by the frame-0 SEED): seed -> roster -> level load
    if (opts.roster !== undefined) {
      ctx.plrm.InitFromRoster(
        opts.roster.map((r) => ({
          pid: r.pid,
          side: r.side,
          name: r.name,
          ready: !!(r.flags & kfPlrReady),
          computer: !!(r.flags & kfPlrComputer),
          overmind: !!(r.flags & kfPlrComputerOvermind),
          unfulfilled: !!(r.flags & kfPlrUnfulfilled),
          creator: !!(r.flags & kfPlrCreator),
          bot: r.bot,
          local: r.local,
        })),
      );
    } else {
      // SP fresh start — game/game.cpp:1664 gplrm.Init(m_miid.szLvlFilename):
      // the C++ loads a THROWAWAY Level just to read the SideInfos
      // (game/Player.cpp:198-208), then allocates per-side players
      // (PlayerMgr::InitFromSideInfos). No RNG draws happen here, so the
      // seed -> roster -> level-load order is preserved.
      const plvlInfo = new Level(ctx);
      if (!plvlInfo.LoadLevelInfo(opts.levelName, new IniReader(opts.levelData.sections))) {
        throw new Error(`Sim.create: LoadLevelInfo failed for ${opts.levelName}`);
      }
      if (!ctx.plrm.InitFromSideInfos(plvlInfo.m_asidi, opts.spHandicap ?? kfHcapDefault)) {
        throw new Error('Sim.create: PlayerMgr.InitFromSideInfos failed');
      }
    }

    // game/game.cpp:1995 → 2010 — Simulation::PerLevelInit (which news the
    // BuildMgr) runs BEFORE gsim.LoadLevel: a level whose [UnitGroups]
    // activate a non-spawn group AT LOAD (CreateAtLevelLoadGroups →
    // UnitGroup::Activate → GetBuildMgr()->BuildUnit) queues builds into the
    // per-level BuildMgr during Level.Init. No main-pack level does this —
    // the first DLC pack played in the browser host crashed here.
    sim.PerLevelInit(new BuildMgr(ctx));

    const plvl = new Level(ctx);
    if (!plvl.Init(opts.levelName, opts.levelData)) {
      throw new Error(`Sim.create: level init failed for ${opts.levelName}`);
    }
    BindLevelToSimulation(ctx, sim, plvl);

    const s = new Sim(ctx, sim, driver);
    if (opts.frames) {
      driver.onFrame = (cUpdates, frameJson) => {
        s.lastFrameJson = frameJson;
        s.onFrame(cUpdates, frameJson);
      };
    }
    driver.onUpdateResult = (block, hash) => {
      s.lastReportedBlock = block; // bookkeeping advances even when not tracking
      if (driver.m_fTrackState) s.onBlockHash(block, hash >>> 0); // but no hash-0 reports from an untracked sim
    };
    return s;
  }

  /** The newest block point whose hash this sim has REPORTED (see stepBlock). */
  private lastReportedBlock = 0;

  /** Feed one recorded update block (cUpdatesBlock + its raw 28-byte wire commands) and
   *  run the lockstep to it. Blocks must arrive in recorded order.
   *  cUpdatesSync acks only what this sim has already HASHED: the recording doesn't
   *  store the live per-message sync points, and acking anything fresher would
   *  ExpireFrames over a still-unhashed window before its dequeue-time GetHash (the
   *  live server's acks always lag the clients' hash reports). */
  stepBlock(cUpdatesBlock: number, rawCmds: Uint8Array[]): void {
    this.queueBlock(cUpdatesBlock, rawCmds);
    this.driver.RunQueued();
  }

  /** Queue a recorded block's commands WITHOUT running its ticks. Pair with stepOnce() to advance
   *  the sim ONE update tick at a time (tick-granular playback — render the in-between game ticks
   *  instead of jumping a whole lockstep block). stepBlock = queueBlock + RunQueued. */
  queueBlock(cUpdatesBlock: number, rawCmds: Uint8Array[]): void {
    this.driver.QueueUpdateMessage({
      cUpdatesBlock,
      cUpdatesSync: this.lastReportedBlock,
      commands: rawCmds.map(decodeMessage),
    });
  }

  /** Advance the sim ONE update tick. Returns false when blocked at a block boundary (queue the next
   *  block via queueBlock, then call again). At a false return both command queues are empty — the
   *  ONLY state snapshot() can capture, since it doesn't serialize the queues. */
  stepOnce(): boolean {
    return this.driver.StepOnce();
  }

  /** The current StateTracker hash — the lockstep comparand. */
  syncHash(): number {
    return this.ctx.tracker.GetHash() >>> 0;
  }

  /** The most recent update's StateFrame::ToJson dict (char-comparable vs C++).
   *  Requires `frames: true` at create/load — frame serialization costs a large
   *  string build EVERY update, so it is opt-in. */
  frameJson(): string {
    if (this.driver.onFrame === null) {
      throw new Error('frameJson(): create the Sim with frames: true to capture per-update frames');
    }
    return this.lastFrameJson;
  }

  updateCount(): number {
    return this.sim.GetUpdateCount();
  }

  // ---- savegames -----------------------------------------------------------------
  // The C++-format sim-core save slice (Game::SaveGame's deterministic tail: the pvars
  // Dictionary + PlayerMgr + Simulation, byte-compatible with game/game.cpp:2478-2487).
  // NOTE the C++ format does NOT carry the sync-RNG seed (SP re-seeds 0x29a on load) or
  // the lockstep block point — both are exposed separately for embedders that resume
  // MP-context streams (rngState/resumeBlockPoint).

  /** Serialize pvars + players + simulation (level, gobs, triggers, groups, messages). */
  saveState(): Uint8Array {
    const pstm = new MemoryStream();
    // dev hook bridge: harnesses arm ctx.saveMsgSpans (and ctx.saveGobOffsets) to get
    // raw-Message byte spans / gob record offsets for the cross-validation masker
    const spans = (this.ctx as unknown as { saveMsgSpans?: unknown }).saveMsgSpans;
    if (spans !== undefined) (pstm as unknown as { msgSpans?: unknown }).msgSpans = spans;
    this.savePvars(pstm);
    (this.ctx.plrm as unknown as { SaveState(p: Stream): boolean }).SaveState(pstm);
    this.sim.SaveState(pstm);
    if (!pstm.IsSuccess()) throw new Error('saveState failed');
    return pstm.bytes();
  }

  // ---- complete TS resume snapshot (issue #96) ----------------------------------
  // snapshot()/restore() are a TS-ONLY pair that captures the FULL sim state for a BIT-EXACT
  // resume — everything the C++ savegame format omits for arbitrary mid-game states (gob-list
  // order, the gid free list, dummy ownership, builder rally, the raw BuildQueue, the player
  // economy accumulators) PLUS the sync RNG, async RNG, lockstep block point and the ctx
  // sequence counters. Unlike saveState() — byte-faithful to the shipping C++, from which a
  // loaded game lawfully CANNOT continue bit-exact (sim-quirks B1/B2/B3/B22 + the uncatalogued
  // free-list/rally/accumulator omissions) — a restore()d sim steps identically to a
  // from-scratch re-sim. Use it for the .wirep viewer's instant seek and MP reconnect-resume.
  snapshot(): Uint8Array {
    const pstm = new MemoryStream();
    pstm.WriteDword(SNAPSHOT_MAGIC);
    pstm.WriteByte(SNAPSHOT_VERSION);
    pstm.WriteDword(this.ctx.rng.GetRandomSeed());
    pstm.WriteDword(this.ctx.rngAsync.GetRandomSeed());
    pstm.WriteDword(this.driver.m_cUpdatesBlock);
    pstm.WriteDword(this.lastReportedBlock);
    pstm.WriteWord(this.ctx.nSeqMoveAside & 0xffff);
    pstm.WriteDword(this.ctx.gnSequenceScorch);
    // The machine-local player (gpplrLocal) — drives fog reveal + local sfx/notifications.
    // PlayerMgr.LoadState would re-derive it as "first human loaded", which need not match the
    // saver's; capture it so a restored sim's per-client state matches the original exactly.
    pstm.WriteWord(this.ctx.plrLocal !== null ? this.ctx.plrLocal.GetId() & 0xffff : 0xffff);
    // v2: the version-gate the saver simmed under (a replay passes its recording's header
    // simVersion). Without it a restored sim ran at the LATEST knVersionSimulation and every
    // version-gated behavior (e.g. the simVersion>=4 miner delivery retry, issue #208) diverged
    // from the from-seed re-sim — the replay viewer's checkpoint restores failed validation on
    // every recording made under an older version.
    pstm.WriteWord(this.ctx.simVersion & 0xffff);
    this.ctx.snapshotComplete = true;
    try {
      this.savePvars(pstm);
      (this.ctx.plrm as unknown as { SaveState(p: Stream): boolean }).SaveState(pstm);
      this.sim.SaveState(pstm);
    } finally {
      this.ctx.snapshotComplete = false;
    }
    // v3: the Overmind decision state (an unsavable gob — see OvermindGob.IsSavable). Written
    // AFTER the sim body so restore() can re-apply it to the Overmind(s) the level re-creates
    // during LoadState. Keyed by owner so multi-Overmind levels round-trip.
    this.saveOvermindStates(pstm);
    // v4: the BuildMgr build entries (builder→UnitGroup "build into group" links) — unsavable in
    // the C++ format, needed so units built after a resume inherit their group stance. See
    // BuildMgr.SnapshotBuildEntries.
    (this.sim.GetBuildMgr() as unknown as BuildMgr | null)?.SnapshotBuildEntries(pstm);
    if (!pstm.IsSuccess()) throw new Error('snapshot failed');
    return pstm.bytes();
  }

  private saveOvermindStates(pstm: Stream): void {
    const oms: OvermindGob[] = [];
    for (let g = this.ctx.gobm.GetFirstGob(); g !== null; g = this.ctx.gobm.GetNextGob(g)) {
      if (g instanceof OvermindGob) oms.push(g);
    }
    pstm.WriteByte(oms.length);
    for (const om of oms) {
      pstm.WriteWord(this.ctx.plrm.SnapshotOwnerCode(om.GetOwner()));
      om.SnapshotDecisionState(pstm);
    }
  }

  private restoreOvermindStates(pstm: Stream): void {
    const byOwner = new Map<number, OvermindGob>();
    for (let g = this.ctx.gobm.GetFirstGob(); g !== null; g = this.ctx.gobm.GetNextGob(g)) {
      if (g instanceof OvermindGob) byOwner.set(this.ctx.plrm.SnapshotOwnerCode(g.GetOwner()), g);
    }
    const cOm = pstm.ReadByte();
    for (let i = 0; i < cOm; i++) {
      const owner = pstm.ReadWord();
      const om = byOwner.get(owner);
      // Read into the matched Overmind, or a throwaway if the owner is unexpectedly absent —
      // either way the stream must advance by exactly one record.
      (om ?? new OvermindGob(this.ctx)).RestoreDecisionState(pstm);
    }
  }

  /** Set the saved view position (Simulation::SetViewPos). View scroll is presentation
   *  state the C++ UI moves freely, but the coords are in the save format — match the
   *  client's values before byte-comparing saves. */
  setViewPos(wxView: number, wyView: number): void {
    this.sim.SetViewPos(wxView, wyView);
  }

  /** The raw sync-RNG LCG state (NOT part of the C++ save format). */
  rngState(): number {
    return this.ctx.rng.GetRandomSeed();
  }
  setRngState(seed: number): void {
    this.ctx.rng.SetRandomSeed(seed | 0);
  }

  /** The pending lockstep block point (driver state, NOT part of the C++ save format). */
  resumeBlockPoint(): number {
    return this.driver.m_cUpdatesBlock;
  }

  // game/misc.cpp:1660-1700 Dictionary::SaveState (knVerDictionaryState 0)
  private savePvars(pstm: Stream): void {
    pstm.WriteByte(0);
    pstm.WriteByte(this.ctx.pvars.size);
    for (const { name, value } of this.ctx.pvars.entriesInOrder()) {
      // head-first (MRU)
      pstm.WriteString(name);
      pstm.WriteString(value);
    }
  }

  private loadPvars(pstm: Stream): boolean {
    this.ctx.pvars.Clear();
    const nVer = pstm.ReadByte();
    if (nVer !== 0) return false;
    const cde = pstm.ReadByte();
    for (let i = 0; i < cde; i++) {
      const name = pstm.ReadString(21 /* kcbPvarNameMax */);
      const value = pstm.ReadString(29 /* kcbPvarValueMax = kcbFilename */);
      this.ctx.pvars.Append(name, value); // LoadState appends tail-wise (file order)
    }
    return pstm.IsSuccess();
  }

  /** Reconstruct a game from saveState() bytes — the C++-FAITHFUL savegame. The roster/seed
   *  come from the SAVE (players are serialized); levelDataFor supplies level vectors by name.
   *
   *  NOT bit-exact-resumable: a game loaded from saveState() lawfully CANNOT continue a
   *  from-scratch trajectory, even with rngState() restored — the shipping C++ format reverses
   *  the gob list, omits the gid free list, drops an idle builder's rally + the per-cycle
   *  player economy accumulators, re-ORs ctor flags, and reassigns dummy-owned gobs
   *  (sim-quirks B1/B2/B3/B22 + uncatalogued omissions). Two loads of the SAME save agree with
   *  each other (savegame.test.ts "load determinism"), but not with a never-saved sim. For a
   *  BIT-EXACT resume (the .wirep viewer's instant seek, MP reconnect) use snapshot()/restore()
   *  instead — a complete TS format that carries all of the above (issue #96). */
  static load(opts: {
    save: Uint8Array;
    levelDataFor: (szLevel: string) => LevelData;
    templates: IniSection[];
    anir: AnirJson;
    gcxTile?: 24 | 32;
    multiplayer?: boolean;
    host?: Partial<SimContext['host']>;
    /** See SimCreateOptions.frames. */
    frames?: boolean;
  }): Sim {
    const ctx = new SimContext();
    ctx.fMultiplayer = opts.multiplayer !== false;
    ctx.animDataFor = makeAnimRegistry(opts.anir, opts.gcxTile, () => ctx.simVersion);
    ctx.levelDataFor = opts.levelDataFor;
    if (opts.host) Object.assign(ctx.host, opts.host);

    const sim = new Simulation(ctx);
    ctx.sim = sim;
    const smm = new GobStateMachineMgr(ctx);
    smm.Init(sim);
    ctx.smm = smm;
    const driver = new SimDriver(ctx, sim);

    if (!InitAllClasses(ctx, new IniReader(opts.templates))) {
      throw new Error('Sim.load: InitAllClasses failed');
    }

    // game/game.cpp:1995 — the C++ runs Simulation::PerLevelInit (which news the
    // BuildMgr) BEFORE gsim.LoadState; a loaded sim without it has no AI build
    // pipeline and crashes the first trigger-activated UnitGroup (caught by review).
    sim.PerLevelInit(new BuildMgr(ctx));

    // game/game.cpp:1633 — the C++ load path re-seeds the sync RNG before LoadState
    // (saves are SP in the C++; the seed is NOT in the format). MP-context resumes
    // overwrite this via setRngState() with the saver's live seed.
    ctx.rng.SetRandomSeed(0x29a);

    const s = new Sim(ctx, sim, driver);
    const pstm = new MemoryStream(opts.save);
    if (!s.loadPvars(pstm)) throw new Error('Sim.load: pvars');
    if (!(ctx.plrm as unknown as { LoadState(p: Stream): boolean }).LoadState(pstm)) {
      throw new Error('Sim.load: PlayerMgr');
    }
    if (!sim.LoadState(pstm, () => new Level(ctx))) throw new Error('Sim.load: Simulation');
    if (!pstm.IsSuccess()) throw new Error('Sim.load: stream failure');

    // resume the lockstep where the save left off: the save happens blocked AT a block
    // point, so the pending point is the next update. The FIRST dequeued block cannot
    // be hashed (the pre-save frame window is not in the save), so its report is
    // suppressed rather than emitting a false hash 0 over the empty tracker.
    driver.m_cUpdatesBlock = sim.GetUpdateCount() + 1;
    driver.m_fSuppressNextReport = true;
    s.lastReportedBlock = sim.GetUpdateCount() + 1;

    if (opts.frames) {
      driver.onFrame = (cUpdates, frameJson) => {
        s.lastFrameJson = frameJson;
        s.onFrame(cUpdates, frameJson);
      };
    }
    driver.onUpdateResult = (block, hash) => {
      s.lastReportedBlock = block; // bookkeeping advances even when not tracking
      if (driver.m_fTrackState) s.onBlockHash(block, hash >>> 0); // but no hash-0 reports from an untracked sim
    };
    return s;
  }

  /** Reconstruct a Sim from snapshot() bytes — a COMPLETE bit-exact resume. Unlike load()
   *  (the C++-faithful savegame), restore() carries every field the C++ format omits, so the
   *  resumed sim steps identically to a from-scratch re-sim; feed the remaining recorded
   *  blocks (strictly after resumeBlockPoint()) to fast-forward. No setRngState() needed —
   *  the snapshot includes the sync + async RNG and the lockstep block point. */
  static restore(opts: {
    snapshot: Uint8Array;
    levelDataFor: (szLevel: string) => LevelData;
    templates: IniSection[];
    anir: AnirJson;
    gcxTile?: 24 | 32;
    multiplayer?: boolean;
    host?: Partial<SimContext['host']>;
    frames?: boolean;
    /** See SimCreateOptions.trackState. */
    trackState?: boolean;
  }): Sim {
    const ctx = new SimContext();
    ctx.fMultiplayer = opts.multiplayer !== false;
    ctx.animDataFor = makeAnimRegistry(opts.anir, opts.gcxTile, () => ctx.simVersion);
    ctx.levelDataFor = opts.levelDataFor;
    if (opts.host) Object.assign(ctx.host, opts.host);

    const sim = new Simulation(ctx);
    ctx.sim = sim;
    const smm = new GobStateMachineMgr(ctx);
    smm.Init(sim);
    ctx.smm = smm;
    const driver = new SimDriver(ctx, sim);
    driver.m_fTrackState = opts.trackState !== false;

    if (!InitAllClasses(ctx, new IniReader(opts.templates))) {
      throw new Error('Sim.restore: InitAllClasses failed');
    }
    sim.PerLevelInit(new BuildMgr(ctx));

    const s = new Sim(ctx, sim, driver);
    const pstm = new MemoryStream(opts.snapshot);
    if (pstm.ReadDword() >>> 0 !== SNAPSHOT_MAGIC)
      throw new Error('Sim.restore: bad snapshot magic');
    if (pstm.ReadByte() !== SNAPSHOT_VERSION) throw new Error('Sim.restore: snapshot version');
    const rngSeed = pstm.ReadDword() | 0;
    const rngAsyncSeed = pstm.ReadDword() | 0;
    const blockPoint = pstm.ReadDword() | 0;
    const lastReported = pstm.ReadDword() | 0;
    const nSeqMoveAside = pstm.ReadWord();
    const gnSequenceScorch = pstm.ReadDword() | 0;
    const localPid = pstm.ReadWord();
    // v2: resume under the saver's version-gate (see snapshot()) — set before any stepping so
    // version-gated branches match the from-seed sim exactly.
    ctx.simVersion = pstm.ReadWord();

    ctx.snapshotComplete = true;
    try {
      if (!s.loadPvars(pstm)) throw new Error('Sim.restore: pvars');
      if (!(ctx.plrm as unknown as { LoadState(p: Stream): boolean }).LoadState(pstm)) {
        throw new Error('Sim.restore: PlayerMgr');
      }
      if (!sim.LoadState(pstm, () => new Level(ctx))) throw new Error('Sim.restore: Simulation');
      // v3: re-apply the Overmind decision state onto the Overmind(s) LoadState just re-created.
      s.restoreOvermindStates(pstm);
      // v4: rebuild the BuildMgr build entries (level init made an empty BuildMgr; UnitGroups are
      // restored, so entries re-link by group index).
      (sim.GetBuildMgr() as unknown as BuildMgr | null)?.RestoreBuildEntries(pstm);
    } finally {
      ctx.snapshotComplete = false;
    }
    if (!pstm.IsSuccess()) throw new Error('Sim.restore: stream failure');

    // resume state: the snapshot carries everything (no caller-side restore needed).
    ctx.rng.SetRandomSeed(rngSeed);
    ctx.rngAsync.SetRandomSeed(rngAsyncSeed);
    ctx.nSeqMoveAside = nSeqMoveAside;
    ctx.gnSequenceScorch = gnSequenceScorch;
    // Force the saved local player (overriding LoadState's "first human" default) so fog +
    // local-only state match the saver exactly. 0xffff = no local player (e.g. a headless re-sim).
    ctx.plrLocal =
      localPid === 0xffff ? null : (ctx.plrm.GetPlayerFromPid(localPid) as typeof ctx.plrLocal);
    driver.m_cUpdatesBlock = blockPoint;
    driver.m_fSuppressNextReport = true;
    s.lastReportedBlock = lastReported;

    if (opts.frames) {
      driver.onFrame = (cUpdates, frameJson) => {
        s.lastFrameJson = frameJson;
        s.onFrame(cUpdates, frameJson);
      };
    }
    driver.onUpdateResult = (block, hash) => {
      s.lastReportedBlock = block; // bookkeeping advances even when not tracking
      if (driver.m_fTrackState) s.onBlockHash(block, hash >>> 0); // but no hash-0 reports from an untracked sim
    };
    return s;
  }

  /** [gid, side, type, wx, wy] for EVERY gob (center coords) — mirrors WiAuto_GobDump
   *  exactly: no unit filter, GetCenter positions; two in-sync sims produce identical
   *  lists, so diffing them on a divergent block pinpoints the offending gob. */
  dumpGobs(): GobDump[] {
    const out: GobDump[] = [];
    const wpt = { wx: 0, wy: 0 };
    for (
      let pgob = this.ctx.gobm.GetFirstGob();
      pgob !== null;
      pgob = this.ctx.gobm.GetNextGob(pgob)
    ) {
      pgob.GetCenter(wpt);
      out.push([pgob.GetId(), pgob.GetSide(), pgob.GetType(), wpt.wx, wpt.wy]);
    }
    return out;
  }
}

export type { LevelData, AnirJson, IniSection };
